/**
 * @file src/components/CreateApiKeyModal/apiKeyScope.ts
 * @exports getApiKeyScope
 * @summary Builds the organization and role scope inherited by a newly generated API key. Super admins use the full organization catalog when loaded, otherwise fall back to me.organizations to avoid an empty scope flash.
 */

import type { MeQuery } from "src/generated/graphql";
import { UserRoleEnum } from "src/types";

type ApiKeyScopeOrganization = { id?: string | null; name?: string | null };

type ApiKeyScope = {
	hasBroadAccess: boolean;
	organizations: Array<{ id: string; name: string; role?: string }>;
};

export const getApiKeyScope = (
	me: MeQuery["me"] | null | undefined,
	availableOrganizations?: ApiKeyScopeOrganization[] | null,
): ApiKeyScope => {
	const rolesByOrganization = new Map(
		(me?.roleAssignment ?? []).map(({ organization, roleV2 }) => [organization?.id, roleV2?.name ?? undefined]),
	);
	const superAdminRole = me?.roleAssignment?.find(({ roleV2 }) => roleV2.name === UserRoleEnum.SuperAdmin)?.roleV2.name;
	const meOrganizations = me?.organizations ?? [];
	const hasLoadedAvailableOrganizations = Boolean(availableOrganizations?.length);
	const accessibleOrganizations =
		superAdminRole && hasLoadedAvailableOrganizations ? availableOrganizations : meOrganizations;
	const organizations = (accessibleOrganizations ?? []).flatMap(({ id, name }) =>
		id && name ? [{ id, name, role: superAdminRole ?? rolesByOrganization.get(id) ?? undefined }] : [],
	);
	const hasElevatedRole = organizations.some(({ role }) => role?.toUpperCase().includes("ADMIN"));

	return { hasBroadAccess: organizations.length > 1 || hasElevatedRole, organizations };
};
